import React from 'react';
import { injectIntl } from 'react-intl';
import AdminShell from './AdminShell';
import { Fieldset, SettingRow, Switch, FormActions, FormCard } from './Form';
import { text } from './peopleCopy';

/*
 * Payment gateways — layar `gateways` paket client, arketipe E.
 *
 * Prototipe punya enam saklar. Yang benar-benar tersimpan di database empat
 * baris PaymentMethods: Cash, Stripe, Wallet, Google Pay. Dua sisanya:
 *
 *   - "Stripe mode" (live/test) bukan saklar. Modenya ditentukan pasangan
 *     kunci Stripe — publishable di SiteSettings, rahasia di config yang tidak
 *     masuk git. Dashboard tidak bisa menyentuh berkas itu, jadi di sini
 *     modenya DITAMPILKAN, tidak diubah.
 *   - "Apple Pay" tidak punya baris sendiri. Di iOS native, Apple Pay berjalan
 *     lewat Stripe yang sama, jadi menyalakan kartu berarti menyalakan Apple
 *     Pay. Saklar terpisah akan berpura-pura ada tombol yang sebenarnya tidak
 *     ada.
 *
 * MENYIMPAN SATU PER SATU, BUKAN SEKALIGUS. Prototipe punya satu tombol "Save
 * gateways" untuk seluruh formulir. updatePayment bekerja per metode dan
 * aturannya bergantung urutan — mematikan Stripe ikut mematikan Wallet dan
 * Google Pay. Karena itu perubahan dikirim berurutan: yang dinyalakan dulu,
 * baru yang dimatikan, supaya "matikan Cash, nyalakan Stripe" tidak pernah
 * melewati keadaan tanpa metode aktif sama sekali. Kalau ada yang ditolak,
 * saklarnya dikembalikan dan alasannya ditampilkan apa adanya.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q = `{
  getPaymentGateways {
    stripeMode status errorMessage
    results { id name paymentName enabled lockedOffReason lockedOnReason }
  }
}`;

const M = `mutation ($id: Int, $status: Boolean) {
  updatePayment(id: $id, status: $status) { status errorMessage }
}`;

const CASH = 1, STRIPE = 2, WALLET = 3, GPAY = 4;

// Keterangan tiap metode. Nama di database singkat ("Credit/Debit"), dan
// layar pengaturan pembayaran bukan tempat menebak apa artinya.
const COPY = {
  [STRIPE]: {
    label: 'Card payments (Stripe)',
    hint: 'Visa and Mastercard, plus Apple Pay on iOS and Google Pay on Android.'
  },
  [GPAY]: {
    label: 'Google Pay',
    hint: 'Android only. Charged through Stripe.'
  },
  [WALLET]: {
    label: 'Bas Recovery wallet',
    hint: 'Stored balance the customer tops up by card.'
  },
  [CASH]: {
    label: 'Cash on completion',
    hint: 'The provider confirms collection in the app.'
  }
};
const COPY_AR = {
  [STRIPE]: { label: 'الدفع بالبطاقة (Stripe)', hint: 'Visa وMastercard، بالإضافة إلى Apple Pay على iOS وGoogle Pay على Android.' },
  [GPAY]: { label: 'Google Pay', hint: 'لأجهزة Android فقط. تتم المحاسبة عبر Stripe.' },
  [WALLET]: { label: 'محفظة Bas Recovery', hint: 'رصيد مخزن يشحنه العميل بالبطاقة.' },
  [CASH]: { label: 'الدفع النقدي عند الإكمال', hint: 'يؤكد مقدم الخدمة التحصيل في التطبيق.' }
};

const MODE = {
  live: ['Live', 'ok', 'Real cards are being charged.'],
  test: ['Test', 'warn', 'No real money moves. Payments in the apps are simulated.'],
  unset: ['Not configured', 'danger', 'No Stripe publishable key is stored, so card payments cannot work.']
};
const MODE_AR = {
  live: ['مباشر', 'ok', 'يتم تحصيل مبالغ حقيقية من البطاقات.'],
  test: ['اختبار', 'warn', 'لا تتحرك أموال حقيقية. عمليات الدفع في التطبيقات تجريبية.'],
  unset: ['غير مهيأ', 'danger', 'لم يتم حفظ مفتاح Stripe العام، لذلك لن يعمل الدفع بالبطاقة.']
};
const LOCK_AR = {
  'This is the only payment method left. Customers could not pay at all.': 'هذه هي طريقة الدفع الوحيدة المتبقية. لن يتمكن العملاء من الدفع.',
  'Card payments are off, so cash is the only way to pay.': 'الدفع بالبطاقة متوقف، لذلك النقد هو طريقة الدفع الوحيدة.',
  'Cash is off, so cards are the only way to pay.': 'الدفع النقدي متوقف، لذلك البطاقات هي طريقة الدفع الوحيدة.',
  'Needs card payments switched on — it is topped up and charged through Stripe.': 'يجب تفعيل الدفع بالبطاقة — يتم الشحن والتحصيل عبر Stripe.'
};

class PaymentGateways extends React.Component {
  state = {
    rows: [], stripeMode: 'unset', draft: {},
    loading: true, error: null, saving: false, notice: null
  };

  locale = () => this.props.intl && this.props.intl.locale;
  tx = (en, ar) => text(this.locale(), en, ar);

  componentDidMount() { this.load(); }

  load = async () => {
    this.setState({ loading: true, error: null });
    const json = await gql(Q).catch(() => null);
    const res = json && json.data && json.data.getPaymentGateways;
    if (!res || res.status !== 200) {
      this.setState({ loading: false, error: (res && res.errorMessage) || this.tx('Could not load payment methods.', 'تعذر تحميل طرق الدفع.') });
      return;
    }
    const draft = {};
    (res.results || []).forEach(r => { draft[r.id] = r.enabled; });
    this.setState({
      rows: res.results || [], stripeMode: res.stripeMode,
      draft, loading: false, saving: false
    });
  };

  set = (id, value) => this.setState(st => ({ draft: { ...st.draft, [id]: value } }));

  reset = () => {
    const draft = {};
    this.state.rows.forEach(r => { draft[r.id] = r.enabled; });
    this.setState({ draft, notice: null });
  };

  changed() {
    return this.state.rows.filter(r => this.state.draft[r.id] !== r.enabled);
  }

  save = async () => {
    const pending = this.changed();
    if (!pending.length) return;
    this.setState({ saving: true, notice: null });

    // Nyalakan dulu, baru matikan. Urutan sebaliknya bisa melewati keadaan
    // tanpa satu pun metode aktif, dan server memang menolaknya di situ.
    const ordered = pending.slice().sort(
      (a, b) => (this.state.draft[b.id] ? 1 : 0) - (this.state.draft[a.id] ? 1 : 0));

    const failed = [];
    for (const row of ordered) {
      const json = await gql(M, { id: row.id, status: this.state.draft[row.id] }).catch(() => null);
      const res = json && json.data && json.data.updatePayment;
      if (!res || res.status !== 200) {
          failed.push({ row, message: (res && res.errorMessage) || this.tx('The change was refused.', 'تم رفض التغيير.') });
      }
    }

    // Memuat ulang, bukan menebak: mematikan Stripe ikut mematikan Wallet dan
    // Google Pay di server, dan hanya server yang tahu hasil akhirnya.
    await this.load();

    this.setState({
      notice: failed.length
        ? {
          tone: 'danger',
          text: failed.map(f => `${(COPY[f.row.id] || {}).label || f.row.name}: ${f.message}`).join(' ')
        }
        : {
          tone: 'info',
            text: this.tx(`${pending.length} payment method${pending.length === 1 ? '' : 's'} updated. The change takes effect at the next checkout in both apps.`, `تم تحديث ${pending.length} من طرق الدفع. سيظهر التغيير عند إتمام الدفع التالي في كلا التطبيقين.`)
        }
    });
  };

  row(id) {
    const r = this.state.rows.find(x => x.id === id);
    if (!r) return null;
    const copy = (this.locale() === 'ar' ? COPY_AR[id] : COPY[id]) || { label: r.name, hint: null };
    const value = this.state.draft[id];
    const locked = value ? r.lockedOffReason : r.lockedOnReason;
    const lockedText = this.locale() === 'ar' ? (LOCK_AR[locked] || locked) : locked;

    return (
      <SettingRow key={id} label={copy.label} hint={copy.hint}
        note={locked ? `${this.tx('Locked', 'مقفل')}: ${lockedText}` : null}>
        <Switch checked={value} disabled={!!locked} label={copy.label}
          onChange={v => this.set(id, v)} />
      </SettingRow>
    );
  }

  render() {
    const { rows, stripeMode, loading, error, saving, notice } = this.state;
    const [modeLabel, modeKind, modeHint] = (this.locale() === 'ar' ? MODE_AR : MODE)[stripeMode] || (this.locale() === 'ar' ? MODE_AR : MODE).unset;
    const pending = this.changed();
    const stripeOff = rows.length && !this.state.draft[STRIPE];

    return (
      <AdminShell
        current="gateways"
        breadcrumb={[this.tx('Money', 'المال'), this.tx('Payment gateways', 'بوابات الدفع')]}
        title={this.tx('Payment gateways', 'بوابات الدفع')}
        lead={this.tx('Methods offered at checkout in both apps.', 'طرق الدفع المتاحة عند إتمام الشراء في كلا التطبيقين.')}
        actions={[
          <a className="btn ghost" href="/siteadmin/payment-gateway" key="c">{this.tx('Open in classic', 'فتح النسخة الكلاسيكية')}</a>
        ]}
      >
        {notice && (
          <div className={`notice ${notice.tone}`}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>{this.tx('Dismiss', 'إخفاء')}</button>
          </div>
        )}

        {stripeMode !== 'live' && !loading && !error && (
          <div className={`notice ${stripeMode === 'unset' ? 'danger' : ''}`}>
            <span className="ni">!</span>
            <span className="nt">{this.tx(`Stripe is in ${modeLabel.toLowerCase()} mode. ${modeHint}`, `Stripe في وضع ${modeLabel}. ${modeHint}`)}</span>
          </div>
        )}

        {error
          ? <div className="notice danger"><span className="ni">!</span><span className="nt">{error}</span></div>
          : loading
            ? <section className="card"><div className="card-b">{this.tx('Loading…', 'جارٍ التحميل…')}</div></section>
            : (
              <FormCard
                actions={
                  <FormActions
                    note={pending.length
                      ? this.tx(`${pending.length} change${pending.length === 1 ? '' : 's'} not saved yet`, `${pending.length} تغييرات لم تُحفظ بعد`)
                      : this.tx('Everything here is saved', 'تم حفظ كل شيء هنا')}
                    dirty={pending.length > 0}
                    busy={saving}
                    onSave={this.save}
                    onCancel={this.reset}
                    saveLabel={this.tx('Save gateways', 'حفظ بوابات الدفع')}
                  />
                }
              >
                <Fieldset title={this.tx('Card payments', 'الدفع بالبطاقة')} rows
                  desc={this.tx('Stripe handles every card charge, and both wallet buttons ride on it.', 'يتولى Stripe جميع عمليات الدفع بالبطاقات، وتعتمد عليه خدمتا المحفظة.') }>
                  {this.row(STRIPE)}
                  <SettingRow
                    label={this.tx('Stripe mode', 'وضع Stripe')}
                    hint={this.tx('Set by the API keys on the server, not from this dashboard.', 'يتم تحديده من مفاتيح API على الخادم، وليس من لوحة التحكم هذه.')}
                  >
                    <span className={`tag ${modeKind}`}><i />{modeLabel}</span>
                  </SettingRow>
                </Fieldset>

                <Fieldset title={this.tx('Wallets', 'المحافظ')} rows
                  desc={this.tx('Both are Stripe buttons. Turning card payments off turns these off too.', 'كلتاهما خدمتا دفع عبر Stripe. إيقاف الدفع بالبطاقة يوقفهما أيضاً.') }>
                  {this.row(GPAY)}
                  {/* Apple Pay tidak punya baris di PaymentMethods dan memang
                      tidak butuh: di iOS native ia berjalan lewat Stripe yang
                      sama. Ditampilkan sebagai keadaan yang dibaca, bukan
                      saklar yang tidak tersambung ke apa pun. */}
                  <SettingRow
                    label={this.tx('Apple Pay', 'Apple Pay')}
                    hint={this.tx('iOS only. There is no separate switch — it is part of Stripe on iOS.', 'لأجهزة iOS فقط. لا يوجد مفتاح منفصل — فهي جزء من Stripe على iOS.')}
                  >
                    <span className={`tag ${stripeOff ? '' : 'ok'}`}>
                      <i />{stripeOff ? this.tx('Off with cards', 'متوقفة مع البطاقات') : this.tx('On with cards', 'مفعلة مع البطاقات')}
                    </span>
                  </SettingRow>
                </Fieldset>

                <Fieldset title={this.tx('Other methods', 'طرق أخرى')} rows>
                  {this.row(WALLET)}
                  {this.row(CASH)}
                </Fieldset>
              </FormCard>
            )}
      </AdminShell>
    );
  }
}

export default injectIntl(PaymentGateways);
